import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  Area,
  AreaChart,
} from 'recharts';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  FileText,
  MessageSquare,
  LogOut,
  User,
  Mail,
  Phone,
  Shield,
  PieChartIcon,
  BarChart3,
  Calendar,
  Download,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Building2,
  RefreshCcw,
  HelpCircle,
  Star,
  Clock,
  Eye,
  FileDown,
  Settings,
  Bell,
  ChevronRight,
  Info,
  AlertTriangle,
  History,
  IndianRupee,
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { BASE_URL as API_URL } from '../utils/apiUrl';

// Official Isprava Wealth Logo
const ISPRAVA_LOGO = "https://customer-assets.emergentagent.com/job_ai-finance-chat-1/artifacts/4qsgtdab_Isprava%20Logo%20final.JPG";

// Color palette for charts
const CHART_COLORS = [
  '#C5A059', '#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', 
  '#ef4444', '#06b6d4', '#ec4899', '#6366f1', '#84cc16'
];

// Format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '₹0';
  if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(2)} K`;
  return `₹${value.toFixed(2)}`;
};

// Disclaimer Banner Component
const DisclaimerBanner = ({ text, type = "info" }) => {
  const icons = {
    info: <Info className="w-4 h-4" />,
    warning: <AlertTriangle className="w-4 h-4" />,
    alert: <AlertCircle className="w-4 h-4" />
  };
  
  return (
    <div className={`text-xs p-3 rounded-lg border flex items-start gap-2 ${
      type === 'warning' ? 'bg-amber-900/20 border-amber-700 text-amber-400' :
      type === 'alert' ? 'bg-red-900/20 border-red-700 text-red-400' :
      'bg-slate-800/50 border-slate-700 text-slate-400'
    }`}>
      {icons[type]}
      <span>{text}</span>
    </div>
  );
};

// Login Component
const PortalLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('email');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const requestOTP = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/portal/auth/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('OTP sent to your email');
        setStep('otp');
        setCountdown(60);
      } else {
        toast.error(data.message || 'Failed to send OTP');
      }
    } catch (err) {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/portal/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Login successful');
        localStorage.setItem('portal_token', data.data.token);
        localStorage.setItem('portal_client', JSON.stringify(data.data.client));
        onLogin(data.data);
      } else {
        toast.error(data.detail || 'Invalid OTP');
      }
    } catch (err) {
      toast.error('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-900 border-slate-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src={ISPRAVA_LOGO} 
              alt="Isprava Wealth" 
              className="h-16 w-auto"
              style={{ imageRendering: 'crisp-edges' }}
            />
          </div>
          <CardTitle className="text-2xl text-white">Client Portal</CardTitle>
          <CardDescription className="text-slate-400">
            SEBI Registered Authorised Person
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {step === 'email' ? (
            <>
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Registered Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-600 text-white"
                    disabled={loading}
                  />
                </div>
              </div>
              <Button
                onClick={requestOTP}
                disabled={loading || !email}
                className="w-full bg-[#C5A059] hover:bg-[#b4914e] text-slate-900"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                Get OTP
              </Button>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Enter OTP sent to {email}</label>
                <Input
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-widest bg-slate-800 border-slate-600 text-white"
                  maxLength={6}
                  disabled={loading}
                />
              </div>
              <Button
                onClick={verifyOTP}
                disabled={loading || otp.length !== 6}
                className="w-full bg-[#C5A059] hover:bg-[#b4914e] text-slate-900"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Verify & Login
              </Button>
              <div className="flex justify-between items-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setStep('email'); setOtp(''); }}
                  className="text-slate-400"
                >
                  Change Email
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={requestOTP}
                  disabled={countdown > 0 || loading}
                  className="text-[#C5A059]"
                >
                  {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                </Button>
              </div>
            </>
          )}
          
          <div className="pt-4 border-t border-slate-700 text-center text-xs text-slate-500">
            <p>OTP valid for 10 minutes. Do not share with anyone.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Client Portal Component
const ClientPortalPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [activeTab, setActiveTab] = useState('portfolio');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [portfolio, setPortfolio] = useState(null);
  const [brokerage, setBrokerage] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [communications, setCommunications] = useState(null);
  const [allocationView, setAllocationView] = useState('asset');
  const [supportQueries, setSupportQueries] = useState(null);
  const [reports, setReports] = useState(null);
  const [consent, setConsent] = useState(null);
  const [profile, setProfile] = useState(null);
  const [aumData, setAumData] = useState(null);
  const [aumHistory, setAumHistory] = useState(null);
  
  // Filters
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  // Dialogs
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ subject: '', message: '', category: 'general' });
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [supportForm, setSupportForm] = useState({ category: 'general', subject: '', description: '' });
  const [submittingQuery, setSubmittingQuery] = useState(false);
  
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [ratingForm, setRatingForm] = useState({ rating: 0, comment: '' });
  const [submittingRating, setSubmittingRating] = useState(false);
  
  const [selectedQueryId, setSelectedQueryId] = useState(null);
  const [queryDetail, setQueryDetail] = useState(null);
  
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportForm, setReportForm] = useState({ type: 'portfolio_snapshot', startDate: '', endDate: '' });
  const [generatingReport, setGeneratingReport] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('portal_token');
    const client = localStorage.getItem('portal_client');
    
    if (token && client) {
      setClientData(JSON.parse(client));
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('portal_token')}`,
    'Content-Type': 'application/json'
  });

  // Fetch portfolio data
  const fetchPortfolio = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/portal/portfolio`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setPortfolio(data);
      } else if (res.status === 401) {
        handleSessionExpired();
      }
    } catch (err) {
      console.error('Error fetching portfolio:', err);
    }
  }, []);

  // Fetch brokerage data
  const fetchBrokerage = useCallback(async () => {
    try {
      let url = `${API_URL}/api/portal/brokerage`;
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setBrokerage(data);
      }
    } catch (err) {
      console.error('Error fetching brokerage:', err);
    }
  }, [dateRange]);

  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    try {
      let url = `${API_URL}/api/portal/transactions`;
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setTransactions(data);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
    }
  }, [dateRange]);

  // Fetch communications
  const fetchCommunications = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/portal/communications`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setCommunications(data);
      }
    } catch (err) {
      console.error('Error fetching communications:', err);
    }
  }, []);

  // Fetch support queries
  const fetchSupportQueries = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/portal/queries`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setSupportQueries(data);
      }
    } catch (err) {
      console.error('Error fetching queries:', err);
    }
  }, []);

  // Fetch reports
  const fetchReports = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/portal/reports`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setReports(data);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
  }, []);

  // Fetch consent
  const fetchConsent = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/portal/consent`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setConsent(data);
      }
    } catch (err) {
      console.error('Error fetching consent:', err);
    }
  }, []);

  // Fetch profile
  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/portal/profile`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  }, []);

  // Fetch AUM
  const fetchAum = useCallback(async () => {
    try {
      const [aumRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/portal/aum`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/portal/aum/history?months=12`, { headers: getAuthHeaders() })
      ]);
      
      if (aumRes.ok) {
        const data = await aumRes.json();
        setAumData(data);
      }
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        setAumHistory(historyData);
      }
    } catch (err) {
      console.error('Error fetching AUM:', err);
    }
  }, []);

  const handleSessionExpired = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_client');
    setIsAuthenticated(false);
    setClientData(null);
    toast.error('Session expired. Please login again.');
  };

  // Load data based on active tab
  useEffect(() => {
    if (!isAuthenticated) return;
    
    if (activeTab === 'portfolio') fetchPortfolio();
    else if (activeTab === 'brokerage') fetchBrokerage();
    else if (activeTab === 'transactions') fetchTransactions();
    else if (activeTab === 'communications') fetchCommunications();
    else if (activeTab === 'support') fetchSupportQueries();
    else if (activeTab === 'reports') fetchReports();
    else if (activeTab === 'settings') {
      fetchConsent();
      fetchProfile();
    }
    else if (activeTab === 'aum') fetchAum();
  }, [isAuthenticated, activeTab, fetchPortfolio, fetchBrokerage, fetchTransactions, fetchCommunications, fetchSupportQueries, fetchReports, fetchConsent, fetchProfile, fetchAum]);

  const handleLogin = (data) => {
    setClientData(data.client);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/portal/auth/logout`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
    } catch (err) {
      // Ignore logout errors
    }
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_client');
    setIsAuthenticated(false);
    setClientData(null);
    toast.success('Logged out successfully');
  };

  const submitFeedback = async () => {
    if (!feedbackForm.subject || !feedbackForm.message) {
      toast.error('Please fill all fields');
      return;
    }
    
    setSubmittingFeedback(true);
    try {
      const res = await fetch(`${API_URL}/api/portal/feedback?subject=${encodeURIComponent(feedbackForm.subject)}&message=${encodeURIComponent(feedbackForm.message)}&category=${feedbackForm.category}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Feedback submitted successfully');
        setShowFeedback(false);
        setFeedbackForm({ subject: '', message: '', category: 'general' });
      } else {
        toast.error('Failed to submit feedback');
      }
    } catch (err) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const submitSupportQuery = async () => {
    if (!supportForm.subject || !supportForm.description) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSubmittingQuery(true);
    try {
      const res = await fetch(`${API_URL}/api/portal/queries`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(supportForm)
      });
      
      if (res.ok) {
        toast.success('Query submitted successfully');
        setShowSupportDialog(false);
        setSupportForm({ category: 'general', subject: '', description: '' });
        fetchSupportQueries();
      } else {
        toast.error('Failed to submit query');
      }
    } catch (err) {
      toast.error('Failed to submit query');
    } finally {
      setSubmittingQuery(false);
    }
  };

  const submitRating = async () => {
    if (ratingForm.rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    setSubmittingRating(true);
    try {
      const res = await fetch(`${API_URL}/api/portal/ratings`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(ratingForm)
      });
      
      if (res.ok) {
        toast.success('Thank you for your rating!');
        setShowRatingDialog(false);
        setRatingForm({ rating: 0, comment: '' });
      } else {
        toast.error('Failed to submit rating');
      }
    } catch (err) {
      toast.error('Failed to submit rating');
    } finally {
      setSubmittingRating(false);
    }
  };

  const generateReport = async () => {
    setGeneratingReport(true);
    try {
      const params = new URLSearchParams();
      params.append('report_type', reportForm.type);
      if (reportForm.startDate) params.append('start_date', reportForm.startDate);
      if (reportForm.endDate) params.append('end_date', reportForm.endDate);
      
      const res = await fetch(`${API_URL}/api/portal/reports/generate?${params.toString()}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        toast.success('Report generated successfully');
        setShowReportDialog(false);
        setReportForm({ type: 'portfolio_snapshot', startDate: '', endDate: '' });
        fetchReports();
      } else {
        toast.error('Failed to generate report');
      }
    } catch (err) {
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const updateConsent = async (channel, status) => {
    try {
      const res = await fetch(`${API_URL}/api/portal/consent`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ channel, status })
      });
      
      if (res.ok) {
        toast.success(`${channel} notifications ${status === 'opt_in' ? 'enabled' : 'disabled'}`);
        fetchConsent();
      } else {
        toast.error('Failed to update preferences');
      }
    } catch (err) {
      toast.error('Failed to update preferences');
    }
  };

  const fetchQueryDetail = async (queryId) => {
    try {
      const res = await fetch(`${API_URL}/api/portal/queries/${queryId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setQueryDetail(data.query);
        setSelectedQueryId(queryId);
      }
    } catch (err) {
      toast.error('Failed to load query details');
    }
  };

  // Show login if not authenticated
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <PortalLogin onLogin={handleLogin} />;
  }

  // Main portal UI
  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src={ISPRAVA_LOGO} 
              alt="Isprava Wealth" 
              className="h-10 w-auto"
              style={{ imageRendering: 'crisp-edges' }}
            />
            <div>
              <h1 className="text-lg font-semibold text-white">ISPRAVA WEALTH</h1>
              <p className="text-xs text-slate-400">Client Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-white">{clientData?.name}</p>
              <p className="text-xs text-slate-400">{clientData?.client_code}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowRatingDialog(true)} title="Rate our service">
              <Star className="w-4 h-4 text-[#C5A059]" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowFeedback(true)} title="Send feedback">
              <MessageSquare className="w-4 h-4 text-slate-400" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} title="Logout">
              <LogOut className="w-4 h-4 text-slate-400" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800 mb-6 flex-wrap">
            <TabsTrigger value="portfolio" className="data-[state=active]:bg-[#C5A059]">
              <PieChartIcon className="w-4 h-4 mr-2" /> Portfolio
            </TabsTrigger>
            <TabsTrigger value="aum" className="data-[state=active]:bg-[#C5A059]">
              <IndianRupee className="w-4 h-4 mr-2" /> AUM
            </TabsTrigger>
            <TabsTrigger value="brokerage" className="data-[state=active]:bg-[#C5A059]">
              <Wallet className="w-4 h-4 mr-2" /> Brokerage
            </TabsTrigger>
            <TabsTrigger value="transactions" className="data-[state=active]:bg-[#C5A059]">
              <FileText className="w-4 h-4 mr-2" /> Transactions
            </TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-[#C5A059]">
              <FileDown className="w-4 h-4 mr-2" /> Reports
            </TabsTrigger>
            <TabsTrigger value="support" className="data-[state=active]:bg-[#C5A059]">
              <HelpCircle className="w-4 h-4 mr-2" /> Support
            </TabsTrigger>
            <TabsTrigger value="communications" className="data-[state=active]:bg-[#C5A059]">
              <MessageSquare className="w-4 h-4 mr-2" /> Inbox
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#C5A059]">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio" className="space-y-6">
            {portfolio ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="pt-6">
                      <p className="text-sm text-slate-400">Total Portfolio Value</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(portfolio.summary.total_value)}</p>
                      <p className="text-xs text-slate-500 mt-1">Indicative / Informational</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="pt-6">
                      <p className="text-sm text-slate-400">Total Invested</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(portfolio.summary.total_invested)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="pt-6">
                      <p className="text-sm text-slate-400">Unrealized P&L</p>
                      <p className={`text-2xl font-bold ${portfolio.summary.unrealized_pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {portfolio.summary.unrealized_pnl >= 0 ? '+' : ''}{formatCurrency(portfolio.summary.unrealized_pnl)}
                      </p>
                      <p className="text-xs text-amber-500 mt-1">Indicative / Informational</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="pt-6">
                      <p className="text-sm text-slate-400">Returns</p>
                      <div className="flex items-center gap-2">
                        {portfolio.summary.unrealized_pnl_percent >= 0 ? (
                          <TrendingUp className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-red-500" />
                        )}
                        <p className={`text-2xl font-bold ${portfolio.summary.unrealized_pnl_percent >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {portfolio.summary.unrealized_pnl_percent >= 0 ? '+' : ''}{portfolio.summary.unrealized_pnl_percent.toFixed(2)}%
                        </p>
                      </div>
                      <p className="text-xs text-amber-500 mt-1">Indicative / Informational</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Allocation Charts */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white">Portfolio Allocation</CardTitle>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={allocationView === 'asset' ? 'default' : 'outline'}
                          onClick={() => setAllocationView('asset')}
                          className={allocationView === 'asset' ? 'bg-[#C5A059]' : ''}
                        >
                          Asset Class
                        </Button>
                        <Button
                          size="sm"
                          variant={allocationView === 'industry' ? 'default' : 'outline'}
                          onClick={() => setAllocationView('industry')}
                          className={allocationView === 'industry' ? 'bg-[#C5A059]' : ''}
                        >
                          <Building2 className="w-4 h-4 mr-1" /> Industry
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Pie Chart */}
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={allocationView === 'asset' ? portfolio.asset_allocation : portfolio.industry_allocation}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={100}
                              label={({ name, percentage }) => `${name}: ${percentage}%`}
                            >
                              {(allocationView === 'asset' ? portfolio.asset_allocation : portfolio.industry_allocation).map((entry, index) => (
                                <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                              formatter={(value) => formatCurrency(value)}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      
                      {/* Bar Chart */}
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={allocationView === 'asset' ? portfolio.asset_allocation : portfolio.industry_allocation}
                            layout="vertical"
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis type="number" tick={{ fill: '#94a3b8' }} tickFormatter={(v) => `${(v/100000).toFixed(0)}L`} />
                            <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8' }} width={100} />
                            <Tooltip
                              contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                              formatter={(value) => [formatCurrency(value), 'Value']}
                            />
                            <Bar dataKey="value" fill="#C5A059" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    
                    {/* Allocation Table */}
                    <div className="mt-6">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-slate-700">
                            <TableHead className="text-slate-400">{allocationView === 'asset' ? 'Asset Class' : 'Industry'}</TableHead>
                            <TableHead className="text-slate-400 text-right">Value</TableHead>
                            <TableHead className="text-slate-400 text-right">Allocation</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(allocationView === 'asset' ? portfolio.asset_allocation : portfolio.industry_allocation).map((item, idx) => (
                            <TableRow key={item.name} className="border-slate-700">
                              <TableCell className="text-white flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }} />
                                {item.name}
                              </TableCell>
                              <TableCell className="text-white text-right">{formatCurrency(item.value)}</TableCell>
                              <TableCell className="text-white text-right">{item.percentage}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>

                {/* Holdings Table */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Holdings</CardTitle>
                    <CardDescription>
                      {portfolio.valuation_date && `As of ${new Date(portfolio.valuation_date).toLocaleDateString()}`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">Security</TableHead>
                          <TableHead className="text-slate-400 text-right">Qty</TableHead>
                          <TableHead className="text-slate-400 text-right">Avg Cost</TableHead>
                          <TableHead className="text-slate-400 text-right">CMP</TableHead>
                          <TableHead className="text-slate-400 text-right">Value</TableHead>
                          <TableHead className="text-slate-400 text-right">P&L (Indicative)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {portfolio.holdings.map((h) => (
                          <TableRow key={h.isin || h.symbol} className="border-slate-700">
                            <TableCell>
                              <p className="text-white font-medium">{h.symbol}</p>
                              <p className="text-xs text-slate-400">{h.security_name}</p>
                            </TableCell>
                            <TableCell className="text-white text-right">{h.quantity}</TableCell>
                            <TableCell className="text-white text-right">₹{h.avg_cost.toFixed(2)}</TableCell>
                            <TableCell className="text-white text-right">₹{h.current_price.toFixed(2)}</TableCell>
                            <TableCell className="text-white text-right">{formatCurrency(h.market_value)}</TableCell>
                            <TableCell className={`text-right ${h.unrealized_pnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                              {h.unrealized_pnl >= 0 ? '+' : ''}{formatCurrency(h.unrealized_pnl)}
                              <span className="text-xs ml-1">({h.unrealized_pnl_percent.toFixed(2)}%)</span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* Disclaimer */}
                <DisclaimerBanner 
                  text={portfolio.disclaimer}
                  type="warning"
                />
              </>
            ) : (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-8 h-8 mx-auto text-[#C5A059] animate-spin mb-4" />
                  <p className="text-slate-400">Loading portfolio...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* AUM Tab */}
          <TabsContent value="aum" className="space-y-6">
            {aumData ? (
              <>
                {/* AUM Summary */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-slate-800 border-slate-700 md:col-span-2">
                    <CardContent className="pt-6">
                      <p className="text-sm text-slate-400">Total AUM</p>
                      <p className="text-3xl font-bold text-white">{formatCurrency(aumData.total_aum)}</p>
                      <p className="text-xs text-slate-500 mt-1">As of {aumData.as_of_date}</p>
                    </CardContent>
                  </Card>
                  {aumData.breakdown?.map((item, idx) => (
                    <Card key={item.asset_class} className="bg-slate-800 border-slate-700">
                      <CardContent className="pt-6">
                        <p className="text-sm text-slate-400">{item.asset_class}</p>
                        <p className="text-xl font-bold text-white">{formatCurrency(item.value)}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* AUM History Chart */}
                {aumHistory && aumHistory.history?.length > 0 && (
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <History className="w-5 h-5" />
                        AUM History (Last 12 Months)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={aumHistory.history}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis dataKey="date" tick={{ fill: '#94a3b8' }} />
                            <YAxis tick={{ fill: '#94a3b8' }} tickFormatter={(v) => formatCurrency(v)} />
                            <Tooltip
                              contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                              formatter={(value) => [formatCurrency(value), 'AUM']}
                            />
                            <Area type="monotone" dataKey="total_aum" stroke="#C5A059" fill="#C5A059" fillOpacity={0.3} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <DisclaimerBanner text={aumData.disclaimer} type="info" />
              </>
            ) : (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-8 h-8 mx-auto text-[#C5A059] animate-spin mb-4" />
                  <p className="text-slate-400">Loading AUM data...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Brokerage Tab */}
          <TabsContent value="brokerage" className="space-y-6">
            {/* Date Filter */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4 items-end">
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">From Date</label>
                    <Input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400 block mb-1">To Date</label>
                    <Input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <Button onClick={fetchBrokerage} className="bg-[#C5A059] hover:bg-[#b4914e]">
                    <RefreshCcw className="w-4 h-4 mr-2" /> Apply
                  </Button>
                </div>
              </CardContent>
            </Card>

            {brokerage ? (
              <>
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="pt-6">
                      <p className="text-sm text-slate-400">Gross Brokerage</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(brokerage.summary.total_gross_brokerage)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="pt-6">
                      <p className="text-sm text-slate-400">Net Brokerage</p>
                      <p className="text-2xl font-bold text-emerald-500">{formatCurrency(brokerage.summary.total_net_brokerage)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="pt-6">
                      <p className="text-sm text-slate-400">Equity Brokerage</p>
                      <p className="text-2xl font-bold text-blue-500">{formatCurrency(brokerage.summary.total_equity_brokerage)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="pt-6">
                      <p className="text-sm text-slate-400">Derivatives Brokerage</p>
                      <p className="text-2xl font-bold text-violet-500">{formatCurrency(brokerage.summary.total_derivatives_brokerage)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Records Table */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Brokerage Records</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">Date</TableHead>
                          <TableHead className="text-slate-400 text-right">Equity Turnover</TableHead>
                          <TableHead className="text-slate-400 text-right">Equity Brokerage</TableHead>
                          <TableHead className="text-slate-400 text-right">F&O Turnover</TableHead>
                          <TableHead className="text-slate-400 text-right">F&O Brokerage</TableHead>
                          <TableHead className="text-slate-400 text-right">Gross</TableHead>
                          <TableHead className="text-slate-400 text-right">Net</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {brokerage.records.map((r, idx) => (
                          <TableRow key={idx} className="border-slate-700">
                            <TableCell className="text-white">{r.date ? new Date(r.date).toLocaleDateString() : '-'}</TableCell>
                            <TableCell className="text-white text-right">{formatCurrency(r.equity_turnover)}</TableCell>
                            <TableCell className="text-blue-400 text-right">{formatCurrency(r.equity_brokerage)}</TableCell>
                            <TableCell className="text-white text-right">{formatCurrency(r.derivatives_turnover)}</TableCell>
                            <TableCell className="text-violet-400 text-right">{formatCurrency(r.derivatives_brokerage)}</TableCell>
                            <TableCell className="text-white text-right">{formatCurrency(r.gross_brokerage)}</TableCell>
                            <TableCell className="text-emerald-400 text-right">{formatCurrency(r.net_brokerage)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <DisclaimerBanner text={brokerage.disclaimer} type="info" />
              </>
            ) : (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-8 h-8 mx-auto text-[#C5A059] animate-spin mb-4" />
                  <p className="text-slate-400">Loading brokerage data...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-6">
            {transactions ? (
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Transaction History</CardTitle>
                  <CardDescription>{transactions.count} transactions found</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Date</TableHead>
                        <TableHead className="text-slate-400">Type</TableHead>
                        <TableHead className="text-slate-400">Symbol</TableHead>
                        <TableHead className="text-slate-400 text-right">Qty</TableHead>
                        <TableHead className="text-slate-400 text-right">Price</TableHead>
                        <TableHead className="text-slate-400 text-right">Value</TableHead>
                        <TableHead className="text-slate-400 text-right">Brokerage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.transactions.map((t) => (
                        <TableRow key={t.id} className="border-slate-700">
                          <TableCell className="text-white">{new Date(t.transaction_date).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Badge className="bg-slate-700">{t.type}</Badge>
                          </TableCell>
                          <TableCell className="text-white">{t.symbol || '-'}</TableCell>
                          <TableCell className="text-white text-right">{t.quantity}</TableCell>
                          <TableCell className="text-white text-right">₹{t.price?.toFixed(2)}</TableCell>
                          <TableCell className="text-white text-right">{formatCurrency(t.total_value)}</TableCell>
                          <TableCell className="text-slate-400 text-right">₹{t.brokerage?.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  <div className="mt-4">
                    <DisclaimerBanner text={transactions.disclaimer} type="info" />
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-8 h-8 mx-auto text-[#C5A059] animate-spin mb-4" />
                  <p className="text-slate-400">Loading transactions...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Reports</CardTitle>
                    <CardDescription>Generate and download reports (read-only)</CardDescription>
                  </div>
                  <Button onClick={() => setShowReportDialog(true)} className="bg-[#C5A059] hover:bg-[#b4914e]">
                    <FileText className="w-4 h-4 mr-2" /> Generate Report
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {reports ? (
                  <>
                    <h4 className="text-sm font-medium text-slate-400 mb-3">Available Report Types</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {reports.available_report_types?.map((rt) => (
                        <div key={rt.id} className="p-4 rounded-lg bg-slate-700/50 border border-slate-600">
                          <p className="text-white font-medium">{rt.name}</p>
                          <p className="text-sm text-slate-400">{rt.description}</p>
                          <Badge className="mt-2 bg-slate-600">{rt.frequency}</Badge>
                        </div>
                      ))}
                    </div>

                    {reports.generated_reports?.length > 0 && (
                      <>
                        <h4 className="text-sm font-medium text-slate-400 mb-3">Generated Reports</h4>
                        <Table>
                          <TableHeader>
                            <TableRow className="border-slate-700">
                              <TableHead className="text-slate-400">Report</TableHead>
                              <TableHead className="text-slate-400">Generated</TableHead>
                              <TableHead className="text-slate-400">Expires</TableHead>
                              <TableHead className="text-slate-400 text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reports.generated_reports.map((r) => (
                              <TableRow key={r.id} className="border-slate-700">
                                <TableCell className="text-white">{r.title}</TableCell>
                                <TableCell className="text-slate-400">{new Date(r.generated_at).toLocaleString()}</TableCell>
                                <TableCell className="text-slate-400">{new Date(r.expires_at).toLocaleDateString()}</TableCell>
                                <TableCell className="text-right">
                                  <Button variant="ghost" size="sm" className="text-[#C5A059]">
                                    <Eye className="w-4 h-4 mr-1" /> View
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-emerald-500">
                                    <Download className="w-4 h-4 mr-1" /> Download
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </>
                    )}
                  </>
                ) : (
                  <div className="py-8 text-center">
                    <Loader2 className="w-6 h-6 mx-auto text-[#C5A059] animate-spin mb-2" />
                    <p className="text-slate-400">Loading reports...</p>
                  </div>
                )}

                <div className="mt-4">
                  <DisclaimerBanner 
                    text="All reports are read-only and for informational purposes only. They do not constitute investment advice."
                    type="warning"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="space-y-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Support Queries</CardTitle>
                    <CardDescription>Get help with your account</CardDescription>
                  </div>
                  <Button onClick={() => setShowSupportDialog(true)} className="bg-[#C5A059] hover:bg-[#b4914e]">
                    <HelpCircle className="w-4 h-4 mr-2" /> New Query
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Disclaimer */}
                <DisclaimerBanner 
                  text="No investment advice will be provided via this channel."
                  type="alert"
                />

                {supportQueries ? (
                  <div className="mt-4 space-y-4">
                    {supportQueries.queries?.length === 0 ? (
                      <div className="py-8 text-center">
                        <HelpCircle className="w-12 h-12 mx-auto text-slate-600 mb-4" />
                        <p className="text-slate-400">No queries yet. Click &quot;New Query&quot; to get started.</p>
                      </div>
                    ) : (
                      supportQueries.queries?.map((q) => (
                        <div 
                          key={q.id} 
                          className="p-4 rounded-lg bg-slate-700/50 border border-slate-600 cursor-pointer hover:border-[#C5A059] transition-colors"
                          onClick={() => fetchQueryDetail(q.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">{q.subject}</p>
                              <p className="text-sm text-slate-400">{q.category} • {new Date(q.created_at).toLocaleDateString()}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={
                                q.status === 'open' ? 'bg-blue-600' :
                                q.status === 'in_progress' ? 'bg-amber-600' :
                                q.status === 'resolved' ? 'bg-emerald-600' :
                                'bg-slate-600'
                              }>
                                {q.status}
                              </Badge>
                              <ChevronRight className="w-5 h-5 text-slate-400" />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <Loader2 className="w-6 h-6 mx-auto text-[#C5A059] animate-spin mb-2" />
                    <p className="text-slate-400">Loading queries...</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Query Detail Dialog */}
            {queryDetail && (
              <Dialog open={!!selectedQueryId} onOpenChange={() => { setSelectedQueryId(null); setQueryDetail(null); }}>
                <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-white">{queryDetail.subject}</DialogTitle>
                    <DialogDescription>
                      {queryDetail.category} • Created {new Date(queryDetail.created_at).toLocaleString()}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 max-h-[400px] overflow-y-auto">
                    {queryDetail.messages?.map((msg) => (
                      <div 
                        key={msg.id}
                        className={`p-3 rounded-lg ${
                          msg.sender_type === 'client' 
                            ? 'bg-[#C5A059]/20 ml-8' 
                            : 'bg-slate-700/50 mr-8'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium text-white">{msg.sender_name}</p>
                          <p className="text-xs text-slate-400">{new Date(msg.timestamp).toLocaleString()}</p>
                        </div>
                        <p className="text-slate-300 text-sm">{msg.message}</p>
                      </div>
                    ))}
                  </div>
                  <DisclaimerBanner 
                    text="No investment advice will be provided via this channel."
                    type="alert"
                  />
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications" className="space-y-6">
            {communications ? (
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Communication Inbox</CardTitle>
                  <CardDescription>{communications.count} messages</CardDescription>
                </CardHeader>
                <CardContent>
                  {communications.communications.length === 0 ? (
                    <div className="py-12 text-center">
                      <MessageSquare className="w-12 h-12 mx-auto text-slate-600 mb-4" />
                      <p className="text-slate-400">No communications yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {communications.communications.map((c) => (
                        <div key={c.id} className="p-4 rounded-lg bg-slate-700/50 border border-slate-600">
                          <div className="flex justify-between items-start mb-2">
                            <Badge className={c.direction === 'inbound' ? 'bg-blue-600' : 'bg-emerald-600'}>
                              {c.direction === 'inbound' ? 'Received' : 'Sent'}
                            </Badge>
                            <span className="text-xs text-slate-400">
                              {new Date(c.created_at).toLocaleString()}
                            </span>
                          </div>
                          {c.subject && <p className="text-white font-medium mb-1">{c.subject}</p>}
                          {c.content && <p className="text-slate-300 text-sm">{c.content}</p>}
                          <div className="mt-2 flex gap-2">
                            <Badge variant="outline" className="text-slate-400">{c.channel}</Badge>
                            {c.status && <Badge variant="outline" className="text-slate-400">{c.status}</Badge>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="py-12 text-center">
                  <Loader2 className="w-8 h-8 mx-auto text-[#C5A059] animate-spin mb-4" />
                  <p className="text-slate-400">Loading communications...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            {/* Profile Section */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <User className="w-5 h-5" /> Profile
                </CardTitle>
                <CardDescription>Your account information (read-only)</CardDescription>
              </CardHeader>
              <CardContent>
                {profile ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-slate-400">Name</label>
                      <p className="text-white">{profile.name}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">Client Code</label>
                      <p className="text-white">{profile.client_code}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">Email</label>
                      <p className="text-white">{profile.email}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">Phone</label>
                      <p className="text-white">{profile.phone}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">PAN</label>
                      <p className="text-white">{profile.pan}</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">KYC Status</label>
                      <Badge className={profile.kyc_status === 'verified' ? 'bg-emerald-600' : 'bg-amber-600'}>
                        {profile.kyc_status}
                      </Badge>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400">Segment</label>
                      <p className="text-white capitalize">{profile.segment}</p>
                    </div>
                    {profile.assigned_rm && (
                      <div className="md:col-span-2">
                        <label className="text-xs text-slate-400">Assigned RM</label>
                        <p className="text-white">{profile.assigned_rm.name}</p>
                        <p className="text-sm text-slate-400">{profile.assigned_rm.email} • {profile.assigned_rm.phone}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="py-4 text-center">
                    <Loader2 className="w-6 h-6 mx-auto text-[#C5A059] animate-spin" />
                  </div>
                )}
                
                <div className="mt-4">
                  <DisclaimerBanner text="Profile information is read-only. Contact your RM to update." type="info" />
                </div>
              </CardContent>
            </Card>

            {/* Consent Management */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Bell className="w-5 h-5" /> Communication Preferences
                </CardTitle>
                <CardDescription>Manage your notification settings</CardDescription>
              </CardHeader>
              <CardContent>
                {consent ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50">
                      <div>
                        <p className="text-white font-medium">Email Notifications</p>
                        <p className="text-sm text-slate-400">Receive updates via email</p>
                      </div>
                      <Switch 
                        checked={consent.channels?.email?.status === 'opt_in'}
                        onCheckedChange={(checked) => updateConsent('email', checked ? 'opt_in' : 'opt_out')}
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50">
                      <div>
                        <p className="text-white font-medium">SMS Notifications</p>
                        <p className="text-sm text-slate-400">Receive updates via SMS</p>
                      </div>
                      <Switch 
                        checked={consent.channels?.sms?.status === 'opt_in'}
                        onCheckedChange={(checked) => updateConsent('sms', checked ? 'opt_in' : 'opt_out')}
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50">
                      <div>
                        <p className="text-white font-medium">WhatsApp Notifications</p>
                        <p className="text-sm text-slate-400">Receive updates via WhatsApp</p>
                      </div>
                      <Switch 
                        checked={consent.channels?.whatsapp?.status === 'opt_in'}
                        onCheckedChange={(checked) => updateConsent('whatsapp', checked ? 'opt_in' : 'opt_out')}
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-lg bg-slate-700/50">
                      <div>
                        <p className="text-white font-medium">Promotional Communications</p>
                        <p className="text-sm text-slate-400">Receive promotional offers</p>
                      </div>
                      <Switch 
                        checked={consent.promotional?.status === 'opt_in'}
                        onCheckedChange={(checked) => updateConsent('promotional', checked ? 'opt_in' : 'opt_out')}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="py-4 text-center">
                    <Loader2 className="w-6 h-6 mx-auto text-[#C5A059] animate-spin" />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Feedback Dialog */}
      <Dialog open={showFeedback} onOpenChange={setShowFeedback}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Submit Feedback</DialogTitle>
            <DialogDescription>We value your feedback. Let us know how we can improve.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Category</label>
              <Select value={feedbackForm.category} onValueChange={(v) => setFeedbackForm({ ...feedbackForm, category: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="service">Service Quality</SelectItem>
                  <SelectItem value="portal">Portal Issue</SelectItem>
                  <SelectItem value="suggestion">Suggestion</SelectItem>
                  <SelectItem value="complaint">Complaint</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Subject</label>
              <Input
                value={feedbackForm.subject}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, subject: e.target.value })}
                placeholder="Brief subject"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Message</label>
              <Textarea
                value={feedbackForm.message}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
                placeholder="Your feedback..."
                className="bg-slate-800 border-slate-600 text-white min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowFeedback(false)}>Cancel</Button>
            <Button onClick={submitFeedback} disabled={submittingFeedback} className="bg-[#C5A059] hover:bg-[#b4914e]">
              {submittingFeedback ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Support Query Dialog */}
      <Dialog open={showSupportDialog} onOpenChange={setShowSupportDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">New Support Query</DialogTitle>
            <DialogDescription>Submit a query to our support team</DialogDescription>
          </DialogHeader>
          <DisclaimerBanner 
            text="No investment advice will be provided via this channel."
            type="alert"
          />
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Category</label>
              <Select value={supportForm.category} onValueChange={(v) => setSupportForm({ ...supportForm, category: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="account">Account</SelectItem>
                  <SelectItem value="portfolio">Portfolio</SelectItem>
                  <SelectItem value="brokerage">Brokerage</SelectItem>
                  <SelectItem value="reports">Reports</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Subject *</label>
              <Input
                value={supportForm.subject}
                onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })}
                placeholder="Brief description of your issue"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Description *</label>
              <Textarea
                value={supportForm.description}
                onChange={(e) => setSupportForm({ ...supportForm, description: e.target.value })}
                placeholder="Please provide details about your query..."
                className="bg-slate-800 border-slate-600 text-white min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSupportDialog(false)}>Cancel</Button>
            <Button onClick={submitSupportQuery} disabled={submittingQuery} className="bg-[#C5A059] hover:bg-[#b4914e]">
              {submittingQuery ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Query
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog open={showRatingDialog} onOpenChange={setShowRatingDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Rate Our Service</DialogTitle>
            <DialogDescription>Your feedback helps us improve</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-center gap-2 py-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRatingForm({ ...ratingForm, rating: star })}
                  className="focus:outline-none"
                >
                  <Star 
                    className={`w-10 h-10 transition-colors ${
                      star <= ratingForm.rating 
                        ? 'text-[#C5A059] fill-[#C5A059]' 
                        : 'text-slate-600'
                    }`}
                  />
                </button>
              ))}
            </div>
            <div>
              <label className="text-sm text-slate-400 block mb-1">Comments (Optional)</label>
              <Textarea
                value={ratingForm.comment}
                onChange={(e) => setRatingForm({ ...ratingForm, comment: e.target.value })}
                placeholder="Tell us more about your experience..."
                className="bg-slate-800 border-slate-600 text-white min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRatingDialog(false)}>Cancel</Button>
            <Button onClick={submitRating} disabled={submittingRating || ratingForm.rating === 0} className="bg-[#C5A059] hover:bg-[#b4914e]">
              {submittingRating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
              Submit Rating
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Generate Report</DialogTitle>
            <DialogDescription>Select report type and date range</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 block mb-1">Report Type</label>
              <Select value={reportForm.type} onValueChange={(v) => setReportForm({ ...reportForm, type: v })}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="portfolio_snapshot">Portfolio Snapshot</SelectItem>
                  <SelectItem value="brokerage_statement">Brokerage Statement</SelectItem>
                  <SelectItem value="transaction_statement">Transaction Statement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Start Date</label>
                <Input
                  type="date"
                  value={reportForm.startDate}
                  onChange={(e) => setReportForm({ ...reportForm, startDate: e.target.value })}
                  className="bg-slate-800 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">End Date</label>
                <Input
                  type="date"
                  value={reportForm.endDate}
                  onChange={(e) => setReportForm({ ...reportForm, endDate: e.target.value })}
                  className="bg-slate-800 border-slate-600 text-white"
                />
              </div>
            </div>
          </div>
          <DisclaimerBanner 
            text="All reports are read-only and for informational purposes only."
            type="info"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowReportDialog(false)}>Cancel</Button>
            <Button onClick={generateReport} disabled={generatingReport} className="bg-[#C5A059] hover:bg-[#b4914e]">
              {generatingReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientPortalPage;
